import { useState } from 'react';
import { ChevronDownSmall, AndroidPlaceholder } from 'ui/icons';
import { TemplatePreviewImageEmptyState } from 'ui/illustrations';
import { pxToRem } from 'utils/convert';
import { validateImage } from 'utils/imageUrlValidator';
import { MAX_CHAR_LIMIT } from '../constants';
import { displayContent, truncateText } from '../../common';
import { OS, TemplateEditViews } from '../../common/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { usePushPreviewContext } from '../context/PushPreviewContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { PushContentDefinition } from '../../../graphql/types';
import {
  expandedNotificationAndroidActionButtonGroup,
  expandedNotificationAndroidWrapper,
  expandedNotificationAttachmentAndroid,
  expandedNotificationAttachmentWrapperAndroid,
  expandedNotificationBubble,
  expandedNotificationBubbleAndroidHeaderAndBodyWrapper,
  notificationAndroidActionButton,
  notificationBubbleAndroidBody,
  notificationBubbleAndroidBodyContent,
  notificationBubbleAndroidBodyContentMsg,
  notificationBubbleAndroidBodyContentMsgTitle,
  notificationBubbleAndroidBodyContentMsgTxt,
  notificationBubbleAndroidHeader,
  notificationBubbleAndroidHeaderTime,
  notificationBubbleAndroidIconWrapper,
} from './styles';

export const ExpandedNotificationAndroid = () => {
  const { liveTemplate } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const {
    messageContentBody,
    messageContentTitle,
    messageActionButtons,
    messageAttachmentUrlAndroid,
  } = usePushPreviewContext();
  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  const usePopulatedField =
    view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const title = usePopulatedField ? messageContentTitle : pushData.title || '';
  const message = usePopulatedField ? messageContentBody : pushData.message || '';
  const attachmentUrlAndroid = usePopulatedField
    ? messageAttachmentUrlAndroid
    : pushData.attachmentUrlAndroid || '';
  const actionButtons = usePopulatedField ? messageActionButtons : pushData.actionButtons || [];
  const [isImageUrlValid, setIsImageUrlValid] = useState<boolean>(false);

  const urlValidation = async () => {
    const isValidImage = await validateImage(attachmentUrlAndroid || '');
    // @ts-ignore
    setIsImageUrlValid(isValidImage);
  };

  urlValidation().then(() => undefined);

  return (
    <div
      data-test="expanded-notification-preview-android"
      style={expandedNotificationAndroidWrapper}
    >
      <div style={expandedNotificationBubble[OS.android]}>
        <div style={expandedNotificationBubbleAndroidHeaderAndBodyWrapper}>
          <div style={notificationBubbleAndroidHeader}>
            <div style={notificationBubbleAndroidHeaderTime}>
              <AndroidPlaceholder height={pxToRem(15)} width={pxToRem(15)} />
              <span
                style={notificationBubbleAndroidIconWrapper}
                data-test={'push-preview-android-app-name'}
              >
                {displayContent.strAppName} &bull; {displayContent.strNow} <ChevronDownSmall />
              </span>
            </div>
          </div>
          <div style={notificationBubbleAndroidBody}>
            <div style={notificationBubbleAndroidBodyContent}>
              <div
                style={notificationBubbleAndroidBodyContentMsgTitle}
                data-test={'push-preview-android-title'}
              >
                {title}
              </div>
              <div style={notificationBubbleAndroidBodyContentMsg}>
                {/* SMS, Push, & WebPush do not support HTML, so do not use
                 * dangerouslySetInnerHTML attr to display content (force text rendering)
                 * Email & InApp DO support HTML and should be displayed with
                 * dangerouslySetInnerHTML={{ __html: removeScript(content) }} so that HTML
                 * renders and scripts are removed prior to rendering
                 */}
                <div
                  style={notificationBubbleAndroidBodyContentMsgTxt}
                  data-test={'push-preview-android-message'}
                >
                  {truncateText(message, MAX_CHAR_LIMIT.android)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {pushData.attachmentUrlAndroid && (
          <div style={expandedNotificationAttachmentWrapperAndroid}>
            {isImageUrlValid ? (
              <img
                data-test={'push-preview-android-image'}
                style={expandedNotificationAttachmentAndroid}
                src={attachmentUrlAndroid}
                alt="Android Push Image"
              />
            ) : (
              <TemplatePreviewImageEmptyState data-test={'push-preview-android-empty-image'} />
            )}
          </div>
        )}
        {actionButtons && actionButtons.length > 0 && (
          <div style={expandedNotificationAndroidActionButtonGroup}>
            {actionButtons.map((button, i) => (
              <div
                data-test={`push-preview-android-action-button-${i}`}
                key={i}
                style={notificationAndroidActionButton}
              >
                {button.title}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
